/* -------------------------------------------------------------------------- */
/*                        FlatpickrDateField                                  */
/* -------------------------------------------------------------------------- */
// date/datetime picker using react-flatpickr (no rsuite, no X-button issues).
// Designed to be a drop-in for react-hook-form: accepts field + fieldstate.

import Flatpickr from 'react-flatpickr';
import { Spanish } from 'flatpickr/dist/l10n/es';
import { english } from 'flatpickr/dist/l10n/default';
import '@styles/react/libs/flatpickr/flatpickr.scss';
import './FlatpickrDateField.scss';
import { useIntl, FormattedMessage } from 'react-intl';
import { FormGroup, Label } from 'reactstrap';
import moment from 'moment';

interface Props {
  field: any;
  fieldstate: {
    error?: { message?: string } | string;
  };
  label?: any;
  required?: boolean;
  showTime?: boolean;
  enableTime?: boolean;
  allowNulls?: boolean; // kept for API compat, no longer renders clear button
  disabled?: boolean;
  minDate?: Date | string;
  maxDate?: Date | string;
}

const OUTPUT_FORMAT = 'YYYY-MM-DD HH:mm:ss';

const FlatpickrDateField = ({
  field,
  fieldstate,
  label,
  required = false,
  showTime = false,
  enableTime = false,
  disabled = false,
  minDate,
  maxDate
}: Props) => {
  const intl = useIntl();
  const locale = intl.locale;
  const hasTime = showTime || enableTime;

  const displayFormat = hasTime ? (locale === 'es' ? 'd M Y H:i:S' : 'd M Y h:i:S K') : 'd M Y';

  const currentDate = field.value ? new Date(field.value) : null;

  const handleChange = (dates: Date[]) => {
    if (!dates || dates.length === 0) {
      field.onChange(null);
      return;
    }
    field.onChange(moment(dates[0]).format(OUTPUT_FORMAT));
  };

  const hasError = !!fieldstate.error;

  return (
    <FormGroup>
      <Label for={field.name}>
        {label || (
          <FormattedMessage id={hasTime ? 'dateInput.selectDateTime' : 'dateInput.selectDate'} />
        )}
        {required && <span className="required">&nbsp;*</span>}
      </Label>

      <Flatpickr
        id={field.name}
        value={currentDate ?? undefined}
        onChange={handleChange}
        onReady={(_d, _s, instance) => {
          instance.calendarContainer?.classList.add('fp-compact');
        }}
        disabled={disabled}
        options={{
          enableTime: hasTime,
          enableSeconds: hasTime,
          dateFormat: displayFormat,
          time_24hr: locale === 'es',
          minDate: minDate ? new Date(minDate) : undefined,
          maxDate: maxDate ? new Date(maxDate) : undefined,
          locale: locale === 'es' ? Spanish : english,
          allowInput: true,
          disableMobile: true
        }}
        className={`form-control sfpanel-fp-input${hasError ? ' is-invalid' : ''}`}
        placeholder={intl.formatMessage({
          id: hasTime ? 'dateInput.selectDateTime' : 'dateInput.selectDate',
          defaultMessage: hasTime ? 'Seleccionar fecha y hora' : 'Seleccionar fecha'
        })}
      />

      {hasError && (
        <div className="invalid-feedback" style={{ display: 'block' }}>
          {typeof fieldstate.error === 'string'
            ? fieldstate.error
            : (fieldstate.error as any)?.message || <FormattedMessage id="common.requiredField" />}
        </div>
      )}
    </FormGroup>
  );
};

export default FlatpickrDateField;
